import { isArray, isEmpty } from 'radash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useWatch } from 'react-hook-form';
import { useShallow } from 'zustand/react/shallow';

import { Loader } from '@/components/core/Loader';
import { Select, SelectContent, SelectTrigger } from '@/components/core/Select';

import { FloatComponent, HTMLComponent, IntegerComponent, StringComponent, TextareaComponent } from '..';
import useFocusOnAttribute from '../hooks/useFocusOnAttribute';
import { useIsAttributeReadonly } from '../hooks/useIsAttributeReadonly';
import { useIsHiddenAttribute } from '../hooks/useIsHiddenAttribute';
import { useOptions } from '../hooks/useOptions';
import { useValidationsStore,Validation } from '../hooks/useValidationsStore';
import { ClearButton } from '../Layout/ClearButton';
import { Layout } from '../Layout/Layout';
import { EditorComponent } from '../types';
import { useDimensionalUserSelection } from './DimensionalUserSelectionContext';
import { DimensionSelectItem } from './DimensionSelectItem';

interface DimensionalComponentProps extends EditorComponent {
    className?: string;
}

const DimensionalComponent = ({
    attribute,
    direction = 'horizontal',
    fieldPath = attribute.name,
    readonly = false,
    hidden = false,
    gridMode = false,
    isKeyAttribute = false,
    beforeAttributeControl,
    className,
    labelClassName,
}: DimensionalComponentProps) => {

    const focusRef = useFocusOnAttribute<HTMLButtonElement>(attribute);

    const { options: dimensions, loading: loadingDimensions } = useOptions({ attribute, item: attribute.item });
    const [selectedDimension, setSelectedDimension] = useState<string | undefined>();
    const dimensionalUserSelection = useDimensionalUserSelection();
    const editedDimensionKeysRef = useRef<Record<string, boolean>>({});
    const seenDimensionKeysRef = useRef<Record<string, boolean>>({});
    const lastValueByKeyRef = useRef<Record<string, unknown>>({});
    const currentFieldPath = selectedDimension ? `${fieldPath}.${selectedDimension}` : fieldPath;
    const currentDimensionValue = useWatch({ name: currentFieldPath });

    hidden = useIsHiddenAttribute(attribute, attribute.name, hidden);

    readonly = useIsAttributeReadonly(attribute, attribute.name, readonly);

    // Mass-update overview: show only keys from the sanitized payload, not the raw form.
    const overviewKeys = dimensionalUserSelection?.overviewKeysByAttribute?.[attribute.name];
    const restrictToOverviewKeys = readonly && !!overviewKeys && overviewKeys.length > 0;
    const displayedDimensions = useMemo(() => {
        if (!restrictToOverviewKeys) {
            return dimensions;
        }

        const filtered = dimensions.filter((dimension) => overviewKeys?.includes(dimension.key));
        return filtered.length > 0 ? filtered : dimensions;
    }, [dimensions, restrictToOverviewKeys, overviewKeys]);

    const previousDimensionKeysRef = useRef<string | null>(null);
    // Only a key picked in the dropdown survives a changed key list; an auto-selected key follows the first
    // key, which is the default language once the filter knows the target market.
    const userSelectedDimensionRef = useRef<string | undefined>();

    useEffect(() => {
        if (loadingDimensions || !displayedDimensions?.length) return;

        const dimensionKeys = displayedDimensions.map(d => d.key).join(',');
        const listChanged = previousDimensionKeysRef.current !== dimensionKeys;

        if (listChanged) {
            previousDimensionKeysRef.current = dimensionKeys;
            const userSelectedKey = userSelectedDimensionRef.current;
            const keepUserSelection =
                userSelectedKey === selectedDimension && displayedDimensions.some(d => d.key === userSelectedKey);
            if (!keepUserSelection) {
                userSelectedDimensionRef.current = undefined;
            }
            const nextKey = keepUserSelection ? selectedDimension : displayedDimensions[0]?.key;
            setSelectedDimension(nextKey);
            if (nextKey && !readonly) {
                dimensionalUserSelection?.setActiveKey(attribute.name, nextKey);
            }
            return;
        }

        if (selectedDimension && displayedDimensions.some(d => d.key === selectedDimension)) return;

        const fallbackKey = displayedDimensions[0]?.key;
        userSelectedDimensionRef.current = undefined;
        setSelectedDimension(fallbackKey);
        if (fallbackKey && !readonly) {
            dimensionalUserSelection?.setActiveKey(attribute.name, fallbackKey);
        }
    }, [displayedDimensions, loadingDimensions, selectedDimension, readonly, attribute.name, dimensionalUserSelection]);

    const handleDimensionChange = (key: string) => {
        if (!readonly && selectedDimension && selectedDimension !== key) {
            if (!editedDimensionKeysRef.current[selectedDimension]) {
                dimensionalUserSelection?.unselectKey(attribute.name, selectedDimension);
            }
        }

        userSelectedDimensionRef.current = key;
        setSelectedDimension(key);
        if (!readonly) {
            dimensionalUserSelection?.setActiveKey(attribute.name, key);
        }
    };

    useEffect(() => {
        if (readonly || !selectedDimension || !dimensionalUserSelection) {
            return;
        }

        if (!seenDimensionKeysRef.current[selectedDimension]) {
            seenDimensionKeysRef.current[selectedDimension] = true;
            lastValueByKeyRef.current[selectedDimension] = currentDimensionValue ?? '';
            return;
        }

        const previous = lastValueByKeyRef.current[selectedDimension];
        if (previous === currentDimensionValue) {
            return;
        }

        lastValueByKeyRef.current[selectedDimension] = currentDimensionValue;
        editedDimensionKeysRef.current[selectedDimension] = true;
        dimensionalUserSelection.selectKey(attribute.name, selectedDimension);
    }, [currentDimensionValue, selectedDimension, readonly, attribute.name, dimensionalUserSelection]);

    const { validationErrors: allValidationErrors, validationWarnings: allValidationWarnings } = useValidationsStore(useShallow(state => ({
        validationErrors: state.validationErrors || {},
        validationWarnings: state.validationWarnings || {},
    })));

    const validationErrors = allValidationErrors[fieldPath] as Validation[] | undefined;
    const validationWarnings = allValidationWarnings[fieldPath] as Validation[] | undefined;
    const hasErrors = !isEmpty(validationErrors);
    const hasWarnings = !isEmpty(validationWarnings);

    /**
     * Returns the type of the attribute that the value of the multi dimensional should have.
     * First it checks the `renderer` params and then falls back to the `valueClass` property.
     */
    const getValueAttributeType = () => {

        const renderer = attribute.params?.renderer || attribute.params?.customRenderer;
        const rendererParams: { [key:string]: string | number } = {};

        if (isArray(renderer)) {
            renderer.forEach(renderingParam => rendererParams[renderingParam.key] = renderingParam.value);
        }

        const rendererType = rendererParams['type'];
        if (rendererType) {
            return rendererType;
        } else {
            // Return `valueClass`
            return attribute.params?.valueClass;
        }

    };

    const renderValueAttribute = useCallback((props: EditorComponent & { key: string }) => {
        const type = getValueAttributeType();

        const fieldValidationErrors = allValidationErrors?.[fieldPath] as Validation[] | undefined;
        const fieldValidationWarnings = allValidationWarnings?.[fieldPath] as Validation[] | undefined;
        const fieldHasErrors = !isEmpty(fieldValidationErrors);
        const fieldHasWarnings = !isEmpty(fieldValidationWarnings);

        switch(type) {
            case 'Float':
                return (
                    <FloatComponent
                        {...props}
                        hasErrors={fieldHasErrors}
                        hasWarnings={fieldHasWarnings}
                        isDimensional={true}
                        gridMode={gridMode}
                        className={className}
                    />
                );
            case 'Number':
            case 'Integer':
                return (
                    <IntegerComponent
                        {...props}
                        hasErrors={fieldHasErrors}
                        hasWarnings={fieldHasWarnings}
                        isDimensional={true}
                        gridMode={gridMode}
                        className={className}
                    />
                );
            case 'MultiLineDimensional':
                return (
                    <TextareaComponent
                        {...props}
                        hasErrors={fieldHasErrors}
                        hasWarnings={fieldHasWarnings}
                        isDimensional={true}
                        gridMode={gridMode}
                        className={className}
                    />
                );
            case 'HTMLDimensional':
                return (
                    <HTMLComponent
                        {...props}
                        hasErrors={fieldHasErrors}
                        hasWarnings={fieldHasWarnings}
                        isDimensional={true}
                        gridMode={gridMode}
                        className={className}
                    />
                );
            default: // and 'String'
                return (
                    <StringComponent
                        {...props}
                        hasErrors={fieldHasErrors}
                        hasWarnings={fieldHasWarnings}
                        isDimensional={true}
                        gridMode={gridMode}
                        className={className}
                    />);
        }
    }, [allValidationErrors, allValidationWarnings, fieldPath, gridMode, className]);

    if (hidden) {
        return null;
    }

    if (loadingDimensions || selectedDimension == undefined) {
        return (
            <Layout attribute={attribute} hideLabel={false} direction={direction} fieldPath={fieldPath} gridMode={gridMode}
                isClearable={true} labelClassName={labelClassName}>
                <Loader />
            </Layout>
        );
    }

    /**
     * TODO:
     * Dimensional attributes can use different types of attributes which is governed by `attribute.params?.valueClass`.
     *
     * For example: Float
     */
    return (
        <Layout
            attribute={attribute}
            hideLabel={gridMode}
            direction={direction}
            fieldPath={fieldPath}
            gridMode={gridMode}
            isKeyAttribute={isKeyAttribute}
            beforeAttributeControl={beforeAttributeControl}
            isClearable={false}
            labelVerticalAlign="top"
        >
            <div className="tw-flex tw-flex-row tw-w-full">
                <div className="tw-w-[80px]">
                    <Select value={selectedDimension} onValueChange={handleDimensionChange}>
                        <SelectTrigger borderless={true} ref={focusRef} className={`tw-w-full lg-font-dynamic tw-text-t-primary tw-h-[36px] ${hasErrors ?
                            'error-input' : hasWarnings ? 'warning-input' : ''} tw-bg-gray-100`}>
                            <span className="tw-text-bold tw-text-black">
                                {selectedDimension}
                            </span>
                        </SelectTrigger>
                        <SelectContent className="tw-w-[500px] tw-max-h-[400px] tw-overflow-y-auto">
                            {displayedDimensions.map(dimension =>
                                <DimensionSelectItem key={dimension.key} dimension={dimension} fieldPath={`${fieldPath}[${dimension.key}]`} attribute={attribute} />)}
                        </SelectContent>
                    </Select>
                </div>
                <div className={`tw-w-full ${gridMode? 'tw-pl-2':''}`}>
                    {
                        renderValueAttribute({
                            key: currentFieldPath,
                            fieldPath: currentFieldPath,
                            hideLabel: true,
                            attribute,
                            readonly,
                        })
                    }
                </div>
                {!readonly && <div className='tw-absolute tw-top-0 tw-right-0 tw-mr-2.5 tw-mt-0'>
                    <ClearButton
                        attributeName={attribute.name}
                        fieldPath={currentFieldPath}
                    />
                </div>}
            </div>
        </Layout>
    );

};

export { DimensionalComponent };
